// 게임 초기화를 담당하는 메인 클래스
class GameInitializer {
  constructor(game) {
    this.game = game;

    // 초기화 서브 시스템들
    // webpack 빌드에서는 window를 통해 접근, 개별 스크립트 로드 시에는 직접 접근
    const ThreeSetupClass =
      typeof ThreeSetup !== "undefined" ? ThreeSetup : window.ThreeSetup;
    const SystemsInitializerClass =
      typeof SystemsInitializer !== "undefined"
        ? SystemsInitializer
        : window.SystemsInitializer;
    const EntitiesInitializerClass =
      typeof EntitiesInitializer !== "undefined"
        ? EntitiesInitializer
        : window.EntitiesInitializer;

    this.threeSetup = new ThreeSetupClass(game);
    this.systemsInitializer = new SystemsInitializerClass(game);
    this.entitiesInitializer = new EntitiesInitializerClass(game);
  }

  // 메인 초기화 프로세스
  async initialize() {
    const timer = Utils.createTimer();

    try {
      Utils.log("GameInitializer.initialize() 시작");

      // 1. 기본 ThreeJS 설정
      Utils.log("1단계: ThreeJS 설정 시작");
      this.updateLoadingProgress(35, "3D 렌더러 준비 중...");
      await this.threeSetup.initialize();
      Utils.log("1단계: ThreeJS 설정 완료");

      // 2. 적 모델 캐시 사전 로딩 (성능 최적화를 위해 초기에 실행)
      // 한국어 주석: 화면 로그 비활성화를 위해 필요한 경우에만 상세 로그 출력
      Utils.log("2단계: 적 모델 로딩 시작");
      this.updateLoadingProgress(45, "적 모델 로딩 중...");
      await this.preloadEnemyModels();
      Utils.log("2단계: 적 모델 로딩 완료");

      // 3. 동료 모델 캐시 사전 로딩 (성능 최적화를 위해 초기에 실행)
      Utils.log("3단계: 동료 모델 로딩 시작");
      this.updateLoadingProgress(55, "동료 모델 로딩 중...");
      await this.preloadCompanionModels();
      Utils.log("3단계: 동료 모델 로딩 완료");

      // 4. 게임 시스템 컴포넌트 초기화
      Utils.log("4단계: 게임 시스템 컴포넌트 초기화 시작");
      this.updateLoadingProgress(65, "게임 시스템 설정 중...");
      await this.systemsInitializer.initialize();
      Utils.log("4단계: 게임 시스템 컴포넌트 초기화 완료");

      // 5. 게임 월드 초기화
      Utils.log("5단계: 게임 월드 초기화 시작");
      this.updateLoadingProgress(70, "게임 월드 생성 중...");
      await this.initializeWorld();
      Utils.log("5단계: 게임 월드 초기화 완료");

      // 6. 게임 엔티티 초기화
      Utils.log("6단계: 게임 엔티티 초기화 시작");
      this.updateLoadingProgress(75, "플레이어 생성 중...");
      await this.entitiesInitializer.initialize();
      Utils.log("6단계: 게임 엔티티 초기화 완료");

      // 7. 시스템 연결
      Utils.log("7단계: 시스템 연결 시작");
      this.updateLoadingProgress(80, "시스템 연결 중...");
      this.systemsInitializer.connectSystems();
      Utils.log("7단계: 시스템 연결 완료");

      // 8. 콜리션 시스템 초기화
      Utils.log("8단계: 콜리션 시스템 초기화 시작");
      this.updateLoadingProgress(85, "충돌 시스템 초기화 중...");
      this.initializeCollisionSystem();
      Utils.log("8단계: 콜리션 시스템 초기화 완료");

      // 9. 게임 타이머 시작
      Utils.log("9단계: 게임 타이머 시작");
      this.startGameTimer();
      Utils.log("9단계: 게임 타이머 시작 완료");

      timer.log("게임 초기화 완료");
      Utils.log("GameInitializer.initialize() 완료");
    } catch (error) {
      Utils.log(
        "GameInitializer.initialize() 중 오류 발생: " + error.message,
        "error"
      );
      Utils.log("에러 스택: " + (error.stack || "스택 없음"), "error");
      throw error;
    }
  }

  // 로딩 진행 상태 업데이트
  updateLoadingProgress(progress, message) {
    // 전역 함수 호출 (main.js에 정의됨)
    if (typeof window.updateLoadingProgress === "function") {
      window.updateLoadingProgress(progress, message);
    }
  }

  // 게임 월드 초기화 (간단한 월드 설정만 남김)
  async initializeWorld() {
    await this.game.sceneManager.init();
  }

  // 적 모델 캐시 사전 로딩 (게임 시작 전에 완료)
  async preloadEnemyModels() {
    try {
      Utils.log(
        "적 모델 캐시 확인: " + (window.enemyModelCache ? "존재함" : "없음")
      );
      // 전역 enemy 모델 캐시를 사용하여 모든 적 모델을 미리 로딩
      await window.enemyModelCache.preloadAllModels();
      Utils.log("적 모델 로딩 성공");
    } catch (error) {
      Utils.log("적 모델 로딩 실패: " + error.message, "error");
      Utils.log("에러 스택: " + (error.stack || "스택 없음"), "error");
      // throw error;
    }
  }

  // 동료 모델 캐시 사전 로딩 (게임 시작 전에 완료)
  async preloadCompanionModels() {
    try {
      Utils.log(
        "동료 모델 캐시 확인: " +
          (window.companionModelCache ? "존재함" : "없음")
      );
      // 전역 companion 모델 캐시를 사용하여 모든 동료 모델을 미리 로딩
      await window.companionModelCache.preloadAllModels();
      Utils.log("동료 모델 로딩 성공");
    } catch (error) {
      Utils.log("동료 모델 로딩 실패: " + error.message, "error");
      Utils.log("에러 스택: " + (error.stack || "스택 없음"), "error");
      // throw error;
    }
  }

  // 콜리션 시스템 초기화
  initializeCollisionSystem() {
    // webpack 빌드에서는 window를 통해 접근, 개별 스크립트 로드 시에는 직접 접근
    const CollisionManagerClass =
      typeof CollisionManager !== "undefined"
        ? CollisionManager
        : window.CollisionManager;
    this.game.collisionManager = new CollisionManagerClass();
  }

  // 게임 타이머 시작
  startGameTimer() {
    if (this.game.uiManager) {
      this.game.uiManager.startGameTimer();
    }
  }
}

// webpack 빌드를 위해 default export 추가
export default GameInitializer;
